"use client";

import { GE } from ".constants/gtmEvents";
import { PATH_NOT_ENOUGH_COINS } from ".constants/links";
import undressConfig from ".constants/undress.config.json";
import { Badge, cn, usePortalContext } from "@cloai/uikit";
import { motion } from "framer-motion";
import { some } from "lodash";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { memo, useCallback, useMemo } from "react";

import {
  useABtestContext,
  useGiftContext,
  useGTMContext,
  useProfileContext,
} from "@/providers";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { MOTION_VARIANTS } from "../vars";
import { STYLES as STYLES_V1 } from "./styles";
import { STYLES as STYLES_V2 } from "./stylesV2";
import { ICardProps } from "./types";
import { useBadge } from "./useBadge";
import { imageSizes, textPlacement, textPlacementV2, textSize } from "./vars";

/**
 * Компонент карточки элемента с поддержкой анимации, бейджей и VIP-статуса
 *
 * @description
 * Компонент отображает карточку элемента со следующими возможностями:
 * - Анимация появления через framer-motion
 * - Бейджи для новых, топовых и подарочных элементов
 * - VIP-оверлей для платных элементов
 * - Локализованные названия
 * - Адаптивные изображения
 * - Поддержка различных вариантов отображения (portrait/landscape)
 *
 * @property {Item} item - Объект элемента (костюм, поза и т.д.)
 * @property {number} index - Индекс элемента в сетке (для анимации)
 * @property {(id: string) => void} onClick - Callback при клике по карточке
 * @property {'portrait' | 'landscape'} variant - Вариант отображения карточки
 * @property {string} [className] - Дополнительные CSS классы
 */
export const ItemCard = memo(
  ({ item, index, onClick, variant, className, ...props }: ICardProps) => {
    // Получение данных из контекстов
    const profile = useProfileContext();
    const { claimedUndressGift } = useGiftContext();
    const { closePortal } = usePortalContext();
    const { sendGTM } = useGTMContext();
    const { AAB_RedesignV1 } = useABtestContext();

    const STYLES = AAB_RedesignV1 ? STYLES_V1 : STYLES_V2;

    // Хуки для локализации и навигации
    const locale = useLocale();
    const router = useRouter();

    /**
     * Проверка, достаточно ли VIP-коинов для VIP-генерации
     */
    const haveVipCoins = useMemo(
      () => (profile?.coins || 0) >= undressConfig.undressCost,
      [profile?.coins],
    );

    // Определение VIP-статуса элемента, если у пользователя не хватает VIP-коинов
    const isVIP = useMemo(
      () => !haveVipCoins && !item.isFree,
      [haveVipCoins, item.isFree],
    );

    /**
     * Проверка является ли элемент подарком
     */
    const isGift = some(
      claimedUndressGift,
      (gift) =>
        (gift.__typename === "CostumeUserBonus" &&
          gift.costumeId === item.id) ||
        (gift.__typename === "PoseUserBonus" && gift.poseId === item.id),
    );

    // Получение параметров бейджа
    const getBadgeProps = useBadge(item, isGift, isVIP);

    /**
     * Обработчик клика по карточке
     */
    const handleClick = useCallback(() => {
      // Если элемент VIP и не является подарком
      if (isVIP && !isGift) {
        sendGTM({ event: GE.FLOW_CLICK_VIP_PRESET__REDIRECT_TO_BUY });
        closePortal();
        router.push(PATH_NOT_ENOUGH_COINS, { scroll: false });
        return;
      }
      onClick(item.id);
    }, [closePortal, isGift, isVIP, item.id, onClick, router, sendGTM]);

    // Рендер компонента
    return (
      <motion.div variants={MOTION_VARIANTS.item} custom={index}>
        <Badge {...getBadgeProps}>
          <div
            className={cn(
              STYLES.card.base,
              STYLES.card.variants[variant],
              item.isTop && STYLES.card.animate,
              className,
            )}
            onClick={handleClick}
            {...props}
          >
            {item.video && item.videoWebm ? (
              <video
                muted
                playsInline
                autoPlay
                loop
                controls={false}
                poster={item.image ?? ""}
                className={STYLES.card.image}
              >
                <source src={item.video} type='video/mp4; codecs="hvc1"' />
                <source src={item.videoWebm} type="video/webm" />
              </video>
            ) : (
              <Image
                src={item.image ?? ""}
                fill
                sizes={imageSizes[variant]}
                alt={getLocalizedName(item, locale)}
                priority
                className={STYLES.card.image}
              />
            )}

            {/* TODO: оставили это на АБ-тест */}
            {/* {isVIP && !isGift && <VIPOverlay />} */}
            <span
              className={cn(
                STYLES.card.text.base,
                textSize[variant],
                AAB_RedesignV1
                  ? textPlacement[variant]
                  : textPlacementV2[variant],
              )}
            >
              {getLocalizedName(item, locale)}
            </span>
          </div>
        </Badge>
      </motion.div>
    );
  },
);

ItemCard.displayName = "ItemCard";
